import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Loader2 } from "lucide-react";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Field } from "../components/admin/settlement/FormBits";
import { credits, errDetail, money, newKey } from "../components/admin/settlement/settlementApi";

const SOURCE = { admin_setting: "Admin setting", server_fallback: "Server fallback", not_configured: "Not configured — credits can't be issued" };

function SettingsCard() {
  const [s, setS] = useState(null);
  const [months, setMonths] = useState("");
  const load = () => credits.settings().then((d) => { setS(d); setMonths(d.admin_expiry_months ?? ""); });
  useEffect(() => { load(); }, []);
  const save = async (body) => {
    try { await credits.saveSettings(body); toast.success("Credit settings saved"); load(); } catch (e) { toast.error(errDetail(e).message); }
  };
  if (!s) return null;
  return (
    <div className="bg-white border border-[#282828]/8 p-5 space-y-4" data-testid="credit-settings-card">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#9A7B4F]">Credit settings</div>
      <div className="text-[12px] text-[#676664]" data-testid="credit-expiry-in-use">
        Expiry in use: <strong className="text-[#282828]">{s.default_expiry_months === null ? "—" : s.default_expiry_months === 0 ? "No expiry" : `${s.default_expiry_months} months`}</strong> · {SOURCE[s.default_expiry_source]}
        {s.server_fallback_expiry_months !== null && <span className="text-[#676664]/60"> (server fallback: {s.server_fallback_expiry_months} months)</span>}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Admin expiry (months, 0 = no expiry)"><Input type="number" min="0" max="120" value={months} onChange={(e) => setMonths(e.target.value)} className="h-9 w-40" data-testid="credit-expiry-input" /></Field>
        <Button onClick={() => save({ default_expiry_months: Number(months) })} disabled={months === ""} className="h-9 bg-[#282828] text-white text-[11px]" data-testid="credit-expiry-save-btn">Save</Button>
        <Button variant="outline" onClick={() => save({ default_expiry_months: 0 })} className="h-9 text-[11px]" data-testid="credit-no-expiry-btn">No expiry</Button>
        <Button variant="outline" onClick={() => save({ use_server_default_expiry: true })} className="h-9 text-[11px]" data-testid="credit-use-server-btn">Use server fallback</Button>
      </div>
      <label className="flex items-center gap-2 text-[12px] text-[#676664]">
        <Switch checked={!!s.send_balance_update_email} onCheckedChange={(v) => save({ send_balance_update_email: v })} data-testid="credit-balance-email-switch" />
        Email the customer their new balance after each use
      </label>
    </div>
  );
}

function IssueCard({ onDone }) {
  const [f, setF] = useState({ booking_id: "", amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(newKey);
  const issue = async () => {
    setBusy(true);
    try {
      const c = await credits.issue({ booking_id: f.booking_id.trim(), amount: Number(f.amount), reason: f.reason, idempotency_key: key });
      toast.success(`Issued ${c.number}`); setF({ booking_id: "", amount: "", reason: "" }); setKey(newKey()); onDone();
    } catch (e) { toast.error(errDetail(e).message); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-[#282828]/8 p-5 space-y-3" data-testid="credit-issue-card">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#9A7B4F]">Issue goodwill credit</div>
      <div className="grid grid-cols-[1fr_110px] gap-2">
        <Field label="Booking ID"><Input value={f.booking_id} onChange={(e) => setF({ ...f, booking_id: e.target.value })} className="h-9" data-testid="credit-issue-booking" /></Field>
        <Field label="Amount $"><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-9" data-testid="credit-issue-amount" /></Field>
      </div>
      <Field label="Reason"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="h-9" data-testid="credit-issue-reason" /></Field>
      <Button onClick={issue} disabled={busy || !f.booking_id || !(Number(f.amount) > 0) || !f.reason.trim()} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px]" data-testid="credit-issue-btn">
        {busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}Issue credit & email customer
      </Button>
    </div>
  );
}

function CreditRow({ c, onDone }) {
  const [use, setUse] = useState({ booking_id: "", amount: "" });
  const [open, setOpen] = useState(false);
  const redeem = async () => {
    try { await credits.redeem(c.id, { booking_id: use.booking_id.trim(), amount: Number(use.amount) }); toast.success("Credit applied"); onDone(); } catch (e) { toast.error(errDetail(e).message); }
  };
  const cancel = async () => {
    const reason = window.prompt(`Cancel ${c.number}? Enter a reason:`);
    if (!reason) return;
    try { await credits.cancel(c.id, reason); toast.success("Credit cancelled"); onDone(); } catch (e) { toast.error(errDetail(e).message); }
  };
  const openable = ["active", "partially_used"].includes(c.status);
  return (
    <>
      <tr className="border-t border-[#282828]/5" data-testid={`credit-row-${c.number}`}>
        <td className="px-3 py-2 font-mono text-[11px]">{c.number}</td>
        <td className="px-3 py-2">{c.customer_name}<div className="text-[10px] text-[#676664]/55">{c.customer_email}</div></td>
        <td className="px-3 py-2 text-right tabular-nums">{money(c.original_amount)}</td>
        <td className="px-3 py-2 text-right tabular-nums font-medium" data-testid={`credit-balance-${c.number}`}>{c.status === "cancelled" ? "—" : money(c.balance)}</td>
        <td className="px-3 py-2"><Badge variant="outline" className="text-[9px] uppercase">{c.status.replace(/_/g, " ")}</Badge></td>
        <td className="px-3 py-2 text-[11px]">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("en-AU") : "No expiry"}</td>
        <td className="px-3 py-2 text-right space-x-1">
          {openable && <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setOpen(!open)} data-testid={`credit-use-btn-${c.number}`}>Use</Button>}
          {openable && <Button size="sm" variant="ghost" className="h-7 text-[10px] text-red-600" onClick={cancel} data-testid={`credit-cancel-btn-${c.number}`}>Cancel</Button>}
        </td>
      </tr>
      {open && (
        <tr className="bg-[#FAF8F0]"><td colSpan={7} className="px-3 py-2">
          <div className="flex gap-2 items-end">
            <Field label="Booking ID (rental fee only)"><Input value={use.booking_id} onChange={(e) => setUse({ ...use, booking_id: e.target.value })} className="h-8 w-56" data-testid={`credit-use-booking-${c.number}`} /></Field>
            <Field label="Amount $"><Input type="number" value={use.amount} onChange={(e) => setUse({ ...use, amount: e.target.value })} className="h-8 w-28" data-testid={`credit-use-amount-${c.number}`} /></Field>
            <Button size="sm" className="h-8 bg-[#282828] text-white text-[10px]" onClick={redeem} data-testid={`credit-use-confirm-${c.number}`}>Apply credit</Button>
          </div>
        </td></tr>
      )}
    </>
  );
}

export default function AdminRentalCredits() {
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [email, setEmail] = useState("");
  const load = useCallback(() => {
    credits.list(email.trim()).then(setRows).catch(() => setRows([]));
    credits.analytics().then(setStats).catch(() => {});
  }, [email]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <AdminLayout title="Mahra Rental Credit" subtitle="Credit can only pay rental fees — never bond, delivery or penalty charges.">
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="credit-stats">
          {[["credits_outstanding_total", "Outstanding (liability)"], ["credits_issued_total", "Issued"], ["credits_redeemed_total", "Used"], ["credits_expired_total", "Expired"]].map(([k, l]) => (
            <div key={k} className="bg-white border border-[#282828]/8 p-4"><div className="text-[10px] uppercase tracking-wider text-[#676664]/60">{l}</div><div className="text-xl text-[#282828] mt-1 tabular-nums" data-testid={`credit-stat-${k}`}>{money(stats[k])}</div></div>
          ))}
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6 mb-8"><SettingsCard /><IssueCard onDone={load} /></div>
      <div className="bg-white border border-[#282828]/8">
        <div className="flex items-center gap-2 p-4 border-b border-[#282828]/8">
          <Gift className="w-4 h-4 text-[#9A7B4F]" />
          <Input placeholder="Filter by customer email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="h-9 max-w-xs" data-testid="credit-filter-email" />
          <Button variant="outline" onClick={load} className="h-9 text-[11px]" data-testid="credit-filter-btn">Search</Button>
        </div>
        {!rows ? <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
          <div className="overflow-x-auto"><table className="w-full text-[12px]" data-testid="credits-table">
            <thead className="bg-[#FAF8F0] text-[#676664]/70 text-[10px] uppercase tracking-wider">
              <tr><th className="px-3 py-2 text-left">Number</th><th className="px-3 py-2 text-left">Customer</th><th className="px-3 py-2 text-right">Issued</th><th className="px-3 py-2 text-right">Balance</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Expires</th><th /></tr>
            </thead>
            <tbody>{rows.length ? rows.map((c) => <CreditRow key={c.id} c={c} onDone={load} />) : <tr><td colSpan={7} className="p-8 text-center text-[#676664]/50">No credits yet</td></tr>}</tbody>
          </table></div>
        )}
      </div>
    </AdminLayout>
  );
}
